import {Box, Counter, Flex, IconWithText, Label} from 'exsportia-components'
import {FC, useCallback, useMemo, useState} from 'react'

import {OfferedAddon} from '../../../api/addons/types'
import {Equipment, ServiceItem} from '../../../api/booking/types'
import {addonMaxQuantity, buildAddonLine, isAddonForParticipant} from '../../../helpers/addon-lines'
import {TranslatorScope} from '../../../locale/types'
import {Button} from '../../../ui/components/button/button'
import {useLocale} from '../../../utils/i18n'
import {useModalActions} from '../../../utils/modals'
import {AddonRow} from './addon-row'

type EditEquipmentsModalProps = {
  currency?: string
  initialEquipments?: Equipment[]
  serviceEquipments?: ServiceItem['equipments']
  handleSubmit: (equipments: Equipment[]) => void
  // Add-ons the service or class offers (Settings > Add-ons). Empty or missing: the pop-up is the old equipment list.
  offeredAddons?: OfferedAddon[]
  participantIndex?: number
  // How many of each add-on the other participants already hold (for add-ons capped across the whole booking).
  addonsBookedElsewhere?: Record<string, number>
}

export const EditEquipmentsModal: FC<EditEquipmentsModalProps> = ({
  currency,
  handleSubmit,
  initialEquipments,
  serviceEquipments,
  offeredAddons,
  participantIndex = 0,
  addonsBookedElsewhere,
}) => {
  const {l} = useLocale()
  const {closeModal} = useModalActions()

  const [equipments, setEquipments] = useState<Equipment[]>(
    (serviceEquipments || []).map((eq) => ({
      name: eq.name,
      value: eq.name,
      label: eq.name,
      price: eq.price,
      quantity: initialEquipments?.find((initialEq) => !initialEq.addonId && initialEq.name === eq.name)?.quantity || 0,
    })),
  )
  // Add-ons this participant can take (a per-booking one sits on the first participant only).
  const addons = useMemo(
    () => (offeredAddons || []).filter((addon) => isAddonForParticipant(addon, participantIndex)),
    [offeredAddons, participantIndex],
  )
  const [addonQuantities, setAddonQuantities] = useState<Record<string, number>>(() =>
    addons.reduce<Record<string, number>>((acc, addon) => {
      const held = initialEquipments?.find((line) => line.addonId === addon.addonId)?.quantity || 0
      acc[addon.addonId] = addon.included ? 1 : held
      return acc
    }, {}),
  )
  const getAddonMax = useCallback(
    (addon: OfferedAddon) => addonMaxQuantity(addon, addonsBookedElsewhere?.[addon.addonId] || 0),
    [addonsBookedElsewhere],
  )
  const handleSetEquipmentQuantity = useCallback(
    (index: number, isPlus: boolean) => {
      const newEquipments = [...equipments]
      newEquipments[index] = {
        ...newEquipments[index],
        quantity: newEquipments[index].quantity + (isPlus ? 1 : -1),
      }
      setEquipments(newEquipments)
    },
    [equipments],
  )
  const equipmentsToDisplay = useMemo(() => {
    return equipments.map((equipment, index) => (
      <Flex
        key={index}
        justifyContent='space-between'
        mb='16px'
        position='relative'
      >
        <Flex
          gap='12px'
          alignItems='center'
          maxWidth={['55%', '70%']}
        >
          <IconWithText text={`${equipment.name},`} />
          <Label settings={{fontSize: '16px'}}>
            {l(`${currency || 'USD'}_symbolic` as TranslatorScope, {value: equipment.price})}
          </Label>
        </Flex>
        <Counter
          min={0}
          onClickMinus={() => handleSetEquipmentQuantity(index, false)}
          onClickPlus={() => handleSetEquipmentQuantity(index, true)}
          value={equipment.quantity}
        />
      </Flex>
    ))
  }, [equipments])
  const onSubmit = useCallback(() => {
    const equipmentToSave = equipments.filter((eq) => eq.quantity > 0)
    const addonLinesToSave = addons
      .map((addon) => ({
        addon,
        quantity: addon.included ? 1 : Math.min(addonQuantities[addon.addonId] || 0, getAddonMax(addon)),
      }))
      .filter(({quantity}) => quantity > 0)
      .map(({addon, quantity}) => buildAddonLine(addon, quantity))
    handleSubmit([...equipmentToSave, ...addonLinesToSave])
  }, [equipments, addons, addonQuantities, getAddonMax, handleSubmit])
  return (
    <Flex
      flexDirection='column'
      mb='16px'
      position='relative'
    >
      {equipmentsToDisplay}
      {addons.length > 0 && (
        <Flex
          flexDirection='column'
          mb='16px'
          data-testid='addons-section'
        >
          {(serviceEquipments || []).length > 0 && (
            <Box mb='16px'>
              <Label settings={{fontSize: '16px'}}>{l('addon_section_title')}</Label>
            </Box>
          )}
          {addons.map((addon) => (
            <AddonRow
              key={addon.addonId}
              addon={addon}
              currency={currency}
              quantity={addonQuantities[addon.addonId] || 0}
              max={getAddonMax(addon)}
              onChange={(quantity) => setAddonQuantities((prev) => ({...prev, [addon.addonId]: quantity}))}
            />
          ))}
        </Flex>
      )}
      <Label>{l('additional_venue_fees')}</Label>
      <Flex
        justifyContent='flex-end'
        mt='16px'
      >
        <Flex
          width={['100%', '50%']}
          gap='16px'
        >
          <Button
            type='secondary'
            onClick={closeModal}
          >
            {l('cancel')}
          </Button>
          <Button onClick={onSubmit}>{l('save')}</Button>
        </Flex>
      </Flex>
    </Flex>
  )
}
